import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useDebouncedCallback} from 'use-debounce';
import Button from '../../../../incase-domain/components/button/Button';
import {IElement} from '../../../../incase-domain/elements';
import {ELEMENT} from '../../../../incase-domain/elements/base/ElementTypes';
import {setHighlightedElementTabAction, setSidePanelContentAction} from '../../../../store/actions/grid';
import {setHighlightedElementIndex} from '../../../../store/actions/slide';
import {SidePanelContent} from '../../../../store/actions/types/grid.types';
import {selectedSlideSelector} from '../../../../store/selectors/slide.selectors';
import {TABS} from '../../../../components/slides/form/part/tabs';
import ElementContextMenuTrigger from '../../contextMenu/ElementContextMenu';
import TooltipWrapper from '../TooltipWrapper';

interface WrapperTooltipProps {
    element: IElement;
    onOptions?: (id: string, silent?: boolean) => void;
}

type TooltipItem =
    | {type: 'modal'; id: string; modal: IElement}
    | {type: 'clickBeforeDisplay'; id: string}
    | {type: 'displayAfterClick'; id: string}
    | {type: 'slideChange'; id: string}
    | {type: 'animation'; id: string}
    | {type: 'hideOnMobile'; id: string};

export function WrapperTooltip(props: WrapperTooltipProps): JSX.Element {
    const {element, onOptions} = props;
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const slide = useSelector(selectedSlideSelector);

    const modals = useMemo(() => {
        if (!element.elementToDisplayAfterClick?.length) return [];
        const modals = slide.elements.filter((el) => el.type === ELEMENT.MODAL);
        if (!modals.length) return [];

        return modals.filter((modal) => element.elementToDisplayAfterClick?.some((id) => id === modal.id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [element.elementToDisplayAfterClick]);

    const hasClickBeforeDisplay = (element.elementToClickForDisplay?.length ?? 0) > 0;
    const hasDisplayAfterClick = (element.elementToDisplayAfterClick?.length ?? 0) > 0;
    const hasSlideChange = 'slideChange' in element && !!(element as IElement & {slideChange?: {slideId: string}}).slideChange?.slideId;
    const hasAnimation = !!element.elementAnimations?.entry || element.elementAnimations?.sequence;
    const hasHideOnMobile = !!element.hideOnMobile;

    const allItems = useMemo(
        (): TooltipItem[] => [
            ...modals.map((modal) => ({type: 'modal' as const, id: modal.id, modal})),
            ...(hasClickBeforeDisplay ? [{type: 'clickBeforeDisplay' as const, id: 'clickBeforeDisplay'}] : []),
            ...(hasDisplayAfterClick ? [{type: 'displayAfterClick' as const, id: 'displayAfterClick'}] : []),
            ...(hasSlideChange ? [{type: 'slideChange' as const, id: 'slideChange'}] : []),
            ...(hasAnimation ? [{type: 'animation' as const, id: 'animation'}] : []),
            ...(hasHideOnMobile ? [{type: 'hideOnMobile' as const, id: 'hideOnMobile'}] : []),
        ],
        [modals, hasClickBeforeDisplay, hasDisplayAfterClick, hasSlideChange, hasAnimation, hasHideOnMobile]
    );

    const [displayItems, setDisplayItems] = useState<TooltipItem[]>(allItems);
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    const findMaxItems = useDebouncedCallback(() => {
        if (!containerRef.current || !contentRef.current || allItems.length === 0) return;

        const content = contentRef.current;

        for (let i = allItems.length; i >= 0; i--) {
            setDisplayItems(allItems.slice(0, i));
            if (content.scrollWidth <= content.clientWidth) break;
        }
    }, 50);

    useEffect(() => {
        findMaxItems();
        const resizeObserver = new ResizeObserver(findMaxItems);

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => resizeObserver.disconnect();
    }, [findMaxItems, allItems]);

    const navigateToAdvancedTab = (): void => {
        const elementIndex = slide.elements.findIndex((el) => el.id === element.id);
        dispatch(setHighlightedElementIndex(elementIndex));
        dispatch(setHighlightedElementTabAction(TABS.ADVANCED.id));
        dispatch(setSidePanelContentAction(SidePanelContent.HIGHLIGHTED_ELEMENT));
    };

    return (
        <div className="c-baseElementWrapper__tooltip" ref={containerRef}>
            <div ref={contentRef} className="c-baseElementWrapper__tooltip_content">
                {displayItems.map((item) => {
                    if (item.type === 'modal') {
                        const modalIndex = slide.elements.findIndex((el) => el.id === item.modal.id);
                        return (
                            <TooltipWrapper
                                key={`${item.id}-tooltip-modal`}
                                id={`tooltip-${item.id}`}
                                content={t('presEditPage.subNavBar.tooltips.modalElementTooltip')}
                            >
                                <ElementContextMenuTrigger element={element} elementIndex={modalIndex}>
                                    <Button
                                        leading="solar:notification-unread-linear"
                                        color="primary"
                                        variant="ghost"
                                        onClick={(e) => {
                                            e?.stopPropagation();
                                            e?.preventDefault();
                                            onOptions?.(item.modal.id);
                                        }}
                                    />
                                </ElementContextMenuTrigger>
                            </TooltipWrapper>
                        );
                    }

                    if (item.type === 'clickBeforeDisplay') {
                        return (
                            <TooltipWrapper
                                key={item.id}
                                id={`tooltip-click-before-display-${element.id}`}
                                content={t('presEditPage.subNavBar.tooltips.clickBeforeDisplayTooltip')}
                            >
                                <Button leading="solar:cursor-linear" color="primary" variant="ghost" onClick={navigateToAdvancedTab} />
                            </TooltipWrapper>
                        );
                    }

                    if (item.type === 'displayAfterClick') {
                        return (
                            <TooltipWrapper
                                key={item.id}
                                id={`tooltip-display-after-click-${element.id}`}
                                content={t('presEditPage.subNavBar.tooltips.displayAfterClickTooltip')}
                            >
                                <Button
                                    leading="solar:cursor-square-linear"
                                    color="primary"
                                    variant="ghost"
                                    onClick={navigateToAdvancedTab}
                                />
                            </TooltipWrapper>
                        );
                    }

                    if (item.type === 'slideChange') {
                        return (
                            <TooltipWrapper
                                key={item.id}
                                id={`tooltip-slide-change-${element.id}`}
                                content={t('presEditPage.subNavBar.tooltips.slideChangeTooltip')}
                            >
                                <Button leading="solar:reorder-linear" color="primary" variant="ghost" onClick={navigateToAdvancedTab} />
                            </TooltipWrapper>
                        );
                    }

                    if (item.type === 'animation') {
                        return (
                            <TooltipWrapper
                                key={item.id}
                                id={`tooltip-animation-${element.id}`}
                                content={t('presEditPage.subNavBar.tooltips.animationTooltip')}
                            >
                                <Button
                                    leading="solar:star-fall-minimalistic-2-linear"
                                    color="primary"
                                    variant="ghost"
                                    onClick={navigateToAdvancedTab}
                                />
                            </TooltipWrapper>
                        );
                    }

                    if (item.type === 'hideOnMobile') {
                        return (
                            <TooltipWrapper
                                key={item.id}
                                id={`tooltip-hide-on-mobile-${element.id}`}
                                content={t('presEditPage.subNavBar.tooltips.hideOnMobileTooltip')}
                            >
                                <Button leading="solar:monitor-linear" color="primary" variant="ghost" onClick={navigateToAdvancedTab} />
                            </TooltipWrapper>
                        );
                    }

                    return null;
                })}
            </div>

            {displayItems.length < allItems.length && (
                <Button color="primary" variant="ghost" disablePointerEvents disableHover>
                    ⋯
                </Button>
            )}
        </div>
    );
}
